import { action, computed, observable, makeObservable } from 'mobx'
import Wkt from 'wicket/wicket'
import api from 'api.js'
import _ from 'lodash'
import constants, { selectionTypes } from 'constants.js'
import { geoJSONBounds, removeHtml, smoothFactor } from 'utils'
import ErrorStore, { catchError } from './ErrorStore'
import Leaflet from 'leaflet'
import { dataStore, uiStore, selectionStore } from './UIStore'
import i18next from '../i18n'

// An individual shape within a shapefile level.
export class UserShapefileShape {
  @observable id
  @observable levelId
  @observable name
  @observable added = false
  @observable.ref geoJSON
  @observable shapefile
  @observable selected = false

  constructor ({ id, levelId, name, geoJSON, shapefile }) {
    makeObservable(this)
    this.id = id
    this.levelId = levelId
    this.name = name || id
    this.geoJSON = geoJSON
    this.shapefile = shapefile
  }

  bounds () { return geoJSONBounds(this.geoJSON) }

  toggleSelected () {
    if (this.shapefile.exceededLimit) {
      this.selected = false
    } else {
      this.selected = !this.selected

      if (this.shapefile.selectedLevel.totalPoints < constants.MAX_PREVIEW_POINTS) {
        this.shapefile.selectedLevel.preview = true
      }

      if (!this.shapefile.selectedLevel.preview) {
        this.geoJSON = null
      }
    }
  }

  @catchError async loadGeometry () {
    const response = await api.getUserShapefileShape(this.levelId, this.id)
    this.parseGeometry(response.data.polygon)
  }

  parseGeometry (geometry) {
    const wkt = new Wkt.Wkt()
    this.geoJSON = wkt.read(geometry).toJson()
  }

  @catchError async add (targetGroupId) {
    const wkt = new window.Wkt.Wkt()
    wkt.read(JSON.stringify(this.geoJSON))
    const result = await api.postGeopolygon({
      name: this.name,
      the_geom: wkt.write(),
      target_group: targetGroupId
    })
    return result.data
  }

  @computed get smoothFactor () {
    return smoothFactor(this.geoJSON)
  }
}

// A data set within a shapefile ZIP archive this user has uploaded
export class UserShapefileLevel {
  @observable id
  @observable name
  @observable shapes
  @observable expanded
  @observable loading = false
  @observable loaded = false
  @observable preview = false
  @observable shapefile
  @observable levelAreaTooLarge = false
  @observable totalPoints = 0

  constructor ({ id, name, shapes, expanded, shapefile }) {
    makeObservable(this)
    this.id = id
    this.name = name || ''
    this.shapes = shapes || []
    this.expanded = expanded || false
    this.shapefile = shapefile
  }

  @catchError async load () {
    if (!this.loaded) {
      this.loading = true
      const response = await api.getUserShapefileLevel(this.id)
      const results = response.data

      this.shapes.replace(_(results).map(shape => new UserShapefileShape({
        id: shape.id,
        name: shape.name || shape.id,
        levelId: this.id,
        shapefile: this.shapefile
      })).sortBy('name').value())

      this.totalPoints = _.sumBy(results, shape => shape.num_points)

      if (this.totalPoints < constants.MAX_PREVIEW_POINTS) {
        await this.previewAll()
      } else {
        this.preview = false
        this.levelAreaTooLarge = true
        uiStore.zoomWhilePreviewing = true
      }

      this.loading = false
      this.loaded = true
    }
  }

  async previewAll () {
    this.preview = true
    const response = await api.getUserShapefileLevelGeometries(this.id)
    this.parseAll(response.data)
  }

  async loadAll () {
    this.loading = true
    if (this.totalPoints > constants.MAX_PREVIEW_POINTS) {
      this.preview = false
      this.levelAreaTooLarge = true
      uiStore.zoomWhilePreviewing = true
    }
    const response = await api.getUserShapefileLevelGeometries(this.id)
    this.parseAll(response.data)
    this.loading = false
    this.loaded = true
  }

  clearGeoValues () {
    _.each(this.shapes, shape => { shape.geoJSON = undefined })
  }

  @action parseAll (data) {
    _.each(data, item => {
      _.find(this.shapes, { id: item.id }).parseGeometry(item.polygon)
    })
  }

  bounds () {
    const bounds = Leaflet.latLngBounds()
    _.each(this.shapes, shape => { bounds.extend(shape.bounds()) })
    return bounds
  }
}

// An individual shapefile ZIP archive this user had uploaded
export class UserShapefile {
  @observable id
  @observable expanded
  @observable levels
  @observable _selectedLevelIndex = 0
  @observable shapefile
  @observable allSelected = false
  @observable group

  constructor ({ id, expanded, loaded, levels, shapefile }) {
    makeObservable(this)
    this.id = id
    this.expanded = expanded || false
    this.loaded = loaded || false
    this.shapefile = shapefile
    this.levels = (levels || []).map(level => new UserShapefileLevel({ ...level, shapefile: this }))
    this.group = selectionStore.data.group
  }

  @computed get selectedLevelIndex () {
    return this._selectedLevelIndex
  }

  set selectedLevelIndex (index) {
    this._selectedLevelIndex = index
  }

  @computed get selectedLevel () {
    if (!_.isNil(this.levels[this.selectedLevelIndex])) {
      return this.levels[this.selectedLevelIndex]
    } else {
      return null
    }
  }

  @computed get selectedShapes () {
    return this.selectedLevel.shapes.filter(shape => shape.selected)
  }

  @action selectAll () {
    if (this.willExceededLimit) return

    _.each(this.selectedLevel.shapes, item => { item.selected = true })
    this.allSelected = true
  }

  @action deselectAll () {
    if (this.selectedLevel.totalPoints > constants.MAX_PREVIEW_POINTS) {
      this.selectedLevel.clearGeoValues()
    }
    _.each(this.selectedLevel.shapes, item => { item.selected = false })
    this.allSelected = false
  }

  @computed get displayName () {
    const filename = _.last(this.shapefile.split('/'))
    const ext = this.shapefile.split('.').pop()
    return filename.replace(/[0-9]{10}.(kml|kmz|zip)$/, `.${ext}`)
  }

  @computed get targetGroup () {
    return this.group ||
      (selectionStore && selectionStore.data && selectionStore.data.group) ||
      (dataStore && dataStore.selectedAddGroup) ||
      (dataStore && dataStore.targetGroups && dataStore.targetGroups[0]) ||
      null
  }

  @computed get selectedCount () {
    if (!this.selectedLevel || !this.selectedLevel.shapes) return 0
    return _.filter(this.selectedLevel.shapes, (s) => s.selected).length
  }

  @computed get exceededLimit () {
    const group = this.targetGroup
    const groupGeopolygonsCount = (group && group.geopolygons) ? group.geopolygons.length : 0
    const maxRegions = (uiStore && uiStore.user && uiStore.user.limits && uiStore.user.limits.max_regions) || 1000
    return this.selectedCount + groupGeopolygonsCount >= maxRegions
  }

  @computed get willExceededLimit () {
    const group = this.targetGroup
    const groupGeopolygonsCount = (group && group.geopolygons) ? group.geopolygons.length : 0
    const maxRegions = (uiStore && uiStore.user && uiStore.user.limits && uiStore.user.limits.max_regions) || 1000
    return this.selectedCount + groupGeopolygonsCount > maxRegions
  }

  @catchError async addMultiple (targetGroupId) {
    const isValidArea = await this.checkIfPolygonSizeIsValid()
    if (!isValidArea) {
      uiStore.areMultipolygonsValid = false
      return []
    }
    uiStore.areMultipolygonsValid = true
    const selectedShapes = this.selectedShapes.map((shape) => {
      const wkt = new window.Wkt.Wkt()
      wkt.read(JSON.stringify(shape.geoJSON))
      return {
        name: shape.name,
        the_geom: wkt.write(),
        target_group: targetGroupId
      }
    })
    const payload = {
      target_group: targetGroupId,
      selected_geopolygons: selectedShapes
    }
    const result = await api.postGeopolygons(payload)
    return result.data
  }

  @catchError async checkIfPolygonSizeIsValid () {
    const { area } = await this.getGeopolygonArea()
    if (area > constants.MULTIPOLYGON_MAX_SIZE) return false
    return true
  }

  @catchError async getGeopolygonArea () {
    const selectedShapes = this.selectedShapes.map((shape) => {
      const wkt = new window.Wkt.Wkt()
      wkt.read(JSON.stringify(shape.geoJSON))
      return wkt.write()
    })
    const result = await api.getGeopolygonArea({ geometries: selectedShapes })
    return result.data
  }

  async loadLevels () {
    await Promise.all((this.levels || []).map(level => level.load()))
  }

  async deleteShapefile () {
    await api.deleteShapefile(this.id)
  }
}

// The top-level list of shapefile ZIP archives this user has uploaded
class UserShapefileList {
  @observable shapefiles = []
  @observable choosingFile = false
  @observable _chosenFile = null
  @observable _selectedFile = null
  @observable invalid = observable.map()
  @observable loading = true
  @observable editMode
  @observable additional_info = []

  constructor () {
    makeObservable(this)
  }

  @computed get chosenFile () {
    return this._chosenFile
  }

  @computed get selectedFile () {
    return this._selectedFile
  }

  @computed get selectedLevel () {
    if (this.selectedFile !== null) {
      return this.selectedFile.selectedLevel
    } else {
      return null
    }
  }

  @computed get hasExceededShapefilesLimit () {
    return uiStore.user.limits.max_shapefiles <= this.shapefiles.length
  }

  async deleteShapefile () {
    const file = this.selectedFile
    this.loading = true
    this.selectedFile = null
    await file.deleteShapefile()
    await this.load()
  }

  set selectedFile (file) {
    this._selectedFile = file
    if (!_.isNil(file)) {
      file.selectedLevelIndex = 0
    }
  }

  set chosenFile (file) {
    if (this.validate(file)) {
      this._chosenFile = file
    } else {
      this.choosingFile = false
    }
  }

  validate (file) {
    this.invalid.clear()
    if (file !== null && file.size > constants.MAX_SHAPEFILE_SIZE) {
      this.invalid.set('file', i18next.t('GLOBAL.SHAPEFILE_TOO_LARGE'))
    }
    if (file !== null && !(/\.(zip|kml|kmz)$/).test(file.name)) {
      this.invalid.set('file', i18next.t('GLOBAL.SHAPEFILE_NOT_ZIP'))
    }

    return this.invalid.size === 0
  }

  @catchError @action async load () {
    this.loading = true
    const response = await api.getUserShapefiles()
    this.shapefiles.replace(response.data.map(result =>
      new UserShapefile(result)
    ))
    dataStore.shapefileCount = response.data.length
    this.loading = false
  }

  async upload () {
    const data = new window.FormData()
    data.append('shapefile', this.chosenFile)
    try {
      await api.postUploadShapefile(data)
      await this.load()
      this.chosenFile = null
      this.choosingFile = false
    } catch (e) {
      this.chosenFile = null
      this.choosingFile = false
      if (e.response && e.response.status === 400) {
        const res = e.response.data
        const message = (res.shapefile || res.non_field_errors)[0]
        this.invalid.set('file', removeHtml(message))
      } else {
        ErrorStore.error(e)
      }
    }
  }

  get selection () {
    return {
      item: this,
      type: selectionTypes.USER_SHAPEFILE_LIST,
      data: { focusedInteraction: false }
    }
  }
}

export default UserShapefileList
